import type { Letter } from '@/lib/api';
import { useDateLocale } from '@/lib/hooks/use-date-locale';
import { routes } from '@/lib/routes';
import { formatDistanceToNow } from 'date-fns';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

interface LetterCardProps {
  letter: Letter;
  index?: number;
  showSender?: boolean;
  showRecipient?: boolean;
}

export function LetterCard({ letter, index = 0, showSender, showRecipient }: LetterCardProps) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const displayUser = showSender ? letter.sender : showRecipient ? letter.recipient : null;
  const displayName = displayUser?.username || 'Unknown';

  const timeAgo = letter.sentAt
    ? formatDistanceToNow(new Date(letter.sentAt), { addSuffix: true, locale: dateLocale })
    : formatDistanceToNow(new Date(letter.createdAt), { addSuffix: true, locale: dateLocale });

  const isUnread = letter.status === 'DELIVERED' && !letter.readAt;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ y: -4 }}
      className="relative"
    >
      <Link to={routes.letter(letter.id)}>
        <div
          className={`bg-white rounded-lg p-5 cursor-pointer transition-all ${
            isUnread ? 'border-l-4 border-l-blue' : ''
          }`}
          style={{
            boxShadow: 'var(--shadow-md)'
          }}
        >
          {/* Status indicator */}
          {isUnread && (
            <div className="absolute top-4 right-4">
              <div className="w-2.5 h-2.5 bg-blue rounded-full" />
            </div>
          )}

          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex items-center gap-3">
              {/* Avatar placeholder */}
              <div className="w-10 h-10 rounded-full bg-gray-warm/50 flex items-center justify-center text-sepia font-medium">
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className={`font-medium ${isUnread ? 'text-ink' : 'text-sepia'}`}>
                  {showSender ? t('letter.from') : t('letter.to')}: {displayName}
                </p>
                <p className="text-xs text-sepia/60">{timeAgo}</p>
              </div>
            </div>
          </div>

          {/* Subject */}
          <h3 className={`text-lg mb-2 ${isUnread ? 'font-semibold text-ink' : 'text-sepia'}`}>
            {letter.subject}
          </h3>

          {/* Preview - extract first text from Tiptap JSON */}
          <p className="text-sm text-sepia/70 line-clamp-2">{extractPreview(letter.content)}</p>

          {/* Status badge */}
          <div className="mt-4 flex items-center gap-2">
            <StatusBadge status={letter.status} deliveredAt={letter.deliveredAt} />
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

function StatusBadge({ status, deliveredAt }: { status: Letter['status']; deliveredAt?: string }) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();

  const statusConfig = {
    DRAFT: { label: t('letter.status.draft'), className: 'bg-gray-warm/30 text-sepia' },
    IN_TRANSIT: {
      label: deliveredAt
        ? `${t('letter.arrivesIn', { date: formatDistanceToNow(new Date(deliveredAt), { addSuffix: true, locale: dateLocale }) })}`
        : t('letter.status.inTransit'),
      className: 'bg-blue/10 text-blue'
    },
    DELIVERED: { label: t('letter.status.delivered'), className: 'bg-sage/20 text-sage' },
    READ: { label: t('letter.status.read'), className: 'bg-sage/20 text-sage' }
  };

  const config = statusConfig[status];

  return (
    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${config.className}`}>
      {config.label}
    </span>
  );
}

function extractPreview(content: unknown): string {
  if (!content) return '';

  try {
    // Handle Tiptap JSON format
    const json = typeof content === 'string' ? JSON.parse(content) : content;
    if (json?.content) {
      const text = json.content
        .filter((node: { type: string }) => node.type === 'paragraph')
        .map(
          (node: { content?: { text?: string }[] }) =>
            node.content?.map((c) => c.text || '').join('') || ''
        )
        .join(' ');
      return text.substring(0, 150) + (text.length > 150 ? '...' : '');
    }
  } catch {
    // If it's plain text
    if (typeof content === 'string') {
      return content.substring(0, 150) + (content.length > 150 ? '...' : '');
    }
  }

  return '';
}
